export type LengthMode = 'lengths' | 'cannedTally';

/** Tally negotiations deep-link with this in place of a length id; it names the mode. */
export const CANNED_TALLY_LENGTH_ID = 'canned_tally';

/**
 * Initial selection and mode for a deep link. Applied as useState initialisers rather than in an
 * effect: the seller queries are declared above that effect and gate on the mode, so a sentinel
 * left in the selection for one render is sent to the backend to cast as a uuid.
 */
export function initialLengthSelection(
  platformProductLengthId?: string | null
): {
  selectedLengthIds: string[];
  mode: LengthMode;
} {
  if (platformProductLengthId === CANNED_TALLY_LENGTH_ID) {
    return { selectedLengthIds: [], mode: 'cannedTally' };
  }
  return {
    selectedLengthIds: platformProductLengthId ? [platformProductLengthId] : [],
    mode: 'lengths',
  };
}

/**
 * One ordering for a set of length ids, so the same lengths picked in a different order are the
 * same filter to every consumer and key the same cache entry.
 */
export function canonicalLengthIds(ids: readonly string[]): string[] {
  return [...ids].sort();
}

/**
 * Canned tally groups by transport vehicle, so the mode cannot stand without one. Vehicles load
 * asynchronously, so "no vehicle yet" has to be told apart from "no vehicle exists" — otherwise a
 * canned-tally deep link is bounced back to lengths before the vehicle it needs has arrived.
 */
export function shouldFallBackToLengths(
  mode: LengthMode,
  transportVehicleId: string | null | undefined,
  isLoadingTransportVehicles: boolean
): boolean {
  return (
    !isLoadingTransportVehicles && mode === 'cannedTally' && !transportVehicleId
  );
}

/** The subset of a PlatformProductLength this module needs to classify it. */
export type ClassifiedLength = {
  id: string;
  inventoryOnly?: boolean | null;
};

export function resolveEffectiveLengthIds(
  selectedLengthIds: string[],
  mode: LengthMode
): string[] {
  // usePaginatedQuery keys on JSON.stringify(filters), so an unordered list would drop the
  // accumulated pages for a result set the React Query cache already holds.
  return mode === 'cannedTally' ? [] : canonicalLengthIds(selectedLengthIds);
}

export function singleSelectedLengthId(
  effectiveLengthIds: string[]
): string | undefined {
  return effectiveLengthIds.length === 1 ? effectiveLengthIds[0] : undefined;
}

export function productIdForQuery(
  effectiveLengthIds: string[],
  platformProductId: string | undefined
): string | undefined {
  return effectiveLengthIds.length === 0 ? platformProductId : undefined;
}

export function inventoryOnlyLengthIds(
  platformProductLengths: readonly (ClassifiedLength | null | undefined)[]
): Set<string> {
  return new Set(
    platformProductLengths.flatMap((ppl) =>
      ppl?.inventoryOnly === true ? [ppl.id] : []
    )
  );
}

/**
 * Whether inventory-only (cut-to) lengths are on show — in the picker, the comparison
 * queries and the vendor drill-down alike.
 *
 * A *named* inventory-only length outranks the reveal toggle: a deep link to a cut-to
 * length has to stay visible as a chip, or it filters the table from behind a control the
 * buyer cannot see. Selecting an ordinary length reveals nothing.
 */
export function revealsInventoryLengths(
  includeInventoryLengths: boolean,
  selectedLengthIds: readonly string[],
  inventoryOnlyIds: ReadonlySet<string>
): boolean {
  return (
    includeInventoryLengths ||
    selectedLengthIds.some((id) => inventoryOnlyIds.has(id))
  );
}

export function withoutInventoryOnlyLengths(
  selectedLengthIds: readonly string[],
  inventoryOnlyIds: ReadonlySet<string>
): string[] {
  return selectedLengthIds.filter((id) => !inventoryOnlyIds.has(id));
}

/**
 * Drops ids the product doesn't offer. A deep link can carry another product's length id
 * (a bookmarked Compare URL, a re-keyed product); left in place it filters every query to
 * nothing while the picker — which only knows this product's lengths — shows no chip for it.
 */
export function knownLengthIds(
  selectedLengthIds: readonly string[],
  platformProductLengths: readonly (ClassifiedLength | null | undefined)[]
): string[] {
  const known = new Set(
    platformProductLengths.flatMap((ppl) => (ppl ? [ppl.id] : []))
  );
  return selectedLengthIds.filter((id) => known.has(id));
}
